import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import { AlarmClock, Circle, ThumbsDown, ThumbsUp } from "lucide-mui";
import type { ReactNode } from "react";
import ReviewToggle, { reviewToggleSx } from "../ReviewToggle.tsx";
import type { ReviewState } from "./types.ts";

const OPTIONS = {
  accepted: { label: "Pursue · P", icon: <ThumbsUp fontSize="small" /> },
  // Blue like the list's "To review" chip.
  pending: {
    label: "To review: take the decision back",
    icon: <Circle fontSize="small" />,
    color: "info" as const,
  },
  disqualified: { label: "Disqualify · D", icon: <ThumbsDown fontSize="small" /> },
};

/** Pursue & snooze, next to the thumbs up: an action, not one of the group's values. */
export interface PursueAndSnooze {
  onClick: (anchor: HTMLElement) => void;
  // Filled while the row is snoozed.
  snoozed: boolean;
  title: ReactNode;
  disabled?: boolean;
}

/**
 * Pursue / to review / disqualify for one reviewed row: the same thumbs as the
 * list's row actions and status chips (a check means Done there), with Pursue &
 * snooze beside the thumbs up when given.
 */
export default function PursueReviewToggle({
  value,
  onChange,
  disabled,
  pursueAndSnooze,
}: {
  value: ReviewState;
  onChange: (next: ReviewState | null) => void;
  disabled?: boolean;
  pursueAndSnooze?: PursueAndSnooze;
}) {
  const snoozeDisabled = disabled || pursueAndSnooze?.disabled;
  return (
    <ReviewToggle
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={OPTIONS}
      afterAccepted={
        pursueAndSnooze && (
          <Tooltip title={snoozeDisabled ? "" : pursueAndSnooze.title}>
            <ToggleButton
              value="snooze"
              aria-label="Pursue & snooze"
              // Set here, not by the group: it never matches the review state.
              selected={pursueAndSnooze.snoozed}
              disabled={!!snoozeDisabled}
              onClick={(e) => {
                // Keeps the group's onChange out of it.
                e.preventDefault();
                pursueAndSnooze.onClick(e.currentTarget);
              }}
              sx={reviewToggleSx("success")}
            >
              <AlarmClock fontSize="small" />
            </ToggleButton>
          </Tooltip>
        )
      }
    />
  );
}
